import React, { useEffect } from 'react';
import { useEventTracking } from '../hooks/useEventTracking';
import {CONTACT_DETAILS} from "@/config/contactDetails.ts";
import {ContactInfo} from "@/components/ContactInfo.tsx";
import { Helmet } from 'react-helmet-async';

export const TermsOfServicePage: React.FC = () => {
    const { trackPageView } = useEventTracking();

    useEffect(() => {
        trackPageView('/terms');
    }, [trackPageView]);

    return (
        <>
            <Helmet>
                <title>{`Terms of Service | ${CONTACT_DETAILS.company.name}`}</title>
                <meta name="description" content={`Terms of Service for ${CONTACT_DETAILS.company.name}. Review our terms and conditions for using our predictive analytics services.`} />
                <meta name="robots" content="noindex, follow" />
                <link rel="canonical" href="https://4castlabs.com/terms" />
                <meta property="og:title" content={`Terms of Service | ${CONTACT_DETAILS.company.name}`} />
                <meta property="og:description" content={`Terms of Service for ${CONTACT_DETAILS.company.name}. Review our terms and conditions.`} />
                <meta property="og:url" content="https://4castlabs.com/terms" />
                <meta property="og:type" content="website" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 py-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white shadow-sm rounded-lg p-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Service</h1>

                    <div className="prose prose-lg max-w-none">
                        <p className="text-gray-600 mb-6">
                            <strong>Effective Date:</strong> January 1, 2025
                        </p>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                            <p className="text-gray-700 mb-4">
                                By accessing or using {CONTACT_DETAILS.company.name}' website and services, you agree to be bound by these Terms of Service
                                ("Terms"). If you do not agree to these Terms, please do not use our services.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Services</h2>
                            <p className="text-gray-700 mb-4">
                                {CONTACT_DETAILS.company.name} provides predictive maintenance solutions, equipment monitoring services, and related
                                consulting services. Our services include:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>Predictive analytics and equipment assessments</li>
                                <li>Maintenance optimization recommendations</li>
                                <li>Equipment monitoring and alerting systems</li>
                                <li>Consulting and implementation services</li>
                                <li>Training and support services</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts and Registration</h2>
                            <p className="text-gray-700 mb-4">
                                To access certain features of our services, you may need to create an account. You agree to:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>Provide accurate, current, and complete information</li>
                                <li>Maintain the security of your account credentials</li>
                                <li>Notify us immediately of any unauthorized use</li>
                                <li>Be responsible for all activities under your account</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Acceptable Use</h2>
                            <p className="text-gray-700 mb-4">You agree to use our services only for lawful purposes and agree not to:</p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>Violate any applicable laws or regulations</li>
                                <li>Infringe on intellectual property rights</li>
                                <li>Transmit harmful, malicious, or disruptive content</li>
                                <li>Attempt to gain unauthorized access to our systems</li>
                                <li>Interfere with the proper functioning of our services</li>
                                <li>Use our services for competitive intelligence or benchmarking</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Intellectual Property Rights</h2>
                            <p className="text-gray-700 mb-4">
                                {CONTACT_DETAILS.company.name} retains all rights, title, and interest in our services, software, algorithms, and
                                related intellectual property. You are granted a limited, non-exclusive license to use our
                                services in accordance with these Terms.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data and Privacy</h2>
                            <p className="text-gray-700 mb-4">
                                Our collection and use of your data is governed by our Privacy Policy, which complies with applicable
                                Canadian privacy laws including the Personal Information Protection Act (PIPA) in British Columbia and Alberta.
                                By using our services, you consent to the collection and use of your data as described in our Privacy Policy.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Service Availability</h2>
                            <p className="text-gray-700 mb-4">
                                While we strive to provide continuous service availability, we do not guarantee that our
                                services will be uninterrupted or error-free. We reserve the right to:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>Suspend or discontinue services for maintenance</li>
                                <li>Modify or update our services and features</li>
                                <li>Set usage limits and restrictions</li>
                                <li>Terminate services with appropriate notice</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Payment and Billing</h2>
                            <p className="text-gray-700 mb-4">
                                For paid services, you agree to pay all applicable fees as specified in your service agreement.
                                Payment terms include:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>Fees are due according to the payment schedule in your agreement</li>
                                <li>Late payments may incur additional charges</li>
                                <li>You are responsible for all applicable taxes, including GST/HST and PST where applicable</li>
                                <li>Refunds are subject to our refund policy and applicable consumer protection laws</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Disclaimers and Limitations</h2>
                            <p className="text-gray-700 mb-4">
                                <strong>SERVICE WARRANTY:</strong> Our services are provided "as is" without warranties of any kind,
                                either express or implied, to the extent permitted by applicable law. We disclaim all warranties,
                                including but not limited to merchantability, fitness for a particular purpose, and non-infringement,
                                subject to applicable consumer protection legislation.
                            </p>
                            <p className="text-gray-700 mb-4">
                                <strong>LIMITATION OF LIABILITY:</strong> To the maximum extent permitted by applicable law, including
                                British Columbia and Alberta consumer protection and business practices legislation, {CONTACT_DETAILS.company.name} shall not
                                be liable for any indirect, incidental, special, consequential, or punitive damages, including but
                                not limited to loss of profits, data, or business interruption.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Indemnification</h2>
                            <p className="text-gray-700 mb-4">
                                You agree to indemnify and hold harmless {CONTACT_DETAILS.company.name} from any claims, damages, losses, or expenses
                                arising from your use of our services or violation of these Terms, to the extent permitted by applicable law.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Termination</h2>
                            <p className="text-gray-700 mb-4">
                                Either party may terminate these Terms at any time, subject to applicable notice requirements under
                                consumer protection legislation. Upon termination:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>Your access to our services will be discontinued</li>
                                <li>All outstanding fees remain due and payable</li>
                                <li>Provisions that should survive termination will remain in effect</li>
                                <li>Data retention will be governed by our Privacy Policy and applicable privacy laws</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Dispute Resolution</h2>
                            <p className="text-gray-700 mb-4">
                                Any disputes arising from these Terms will be resolved in accordance with the laws of British Columbia, Canada.
                                We encourage resolution through direct communication first. If formal dispute resolution is necessary,
                                disputes may be resolved through:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                                <li>Mediation through the British Columbia International Commercial Arbitration Centre (BCICAC)</li>
                                <li>The courts of British Columbia, Canada, which shall have exclusive jurisdiction</li>
                                <li>Consumer protection processes available under British Columbia or Alberta consumer protection legislation, where applicable</li>
                            </ul>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Governing Law</h2>
                            <p className="text-gray-700 mb-4">
                                These Terms are governed by and construed in accordance with the laws of British Columbia, Canada,
                                and the federal laws of Canada applicable therein, without regard to conflict of law principles.
                                Where our services are provided to customers in Alberta, the applicable consumer protection laws
                                of Alberta shall also apply to the extent they provide greater protection than these Terms.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Changes to Terms</h2>
                            <p className="text-gray-700 mb-4">
                                We reserve the right to modify these Terms at any time, subject to applicable notice requirements.
                                We will notify users of material changes by posting the updated Terms on our website and updating
                                the effective date. For significant changes that may adversely affect your rights, we will provide
                                advance notice as required by applicable consumer protection laws. Continued use of
                                our services after changes constitutes acceptance of the new Terms.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Consumer Rights</h2>
                            <p className="text-gray-700 mb-4">
                                Nothing in these Terms is intended to limit or exclude any consumer rights that cannot be excluded
                                under the Consumer Protection Act (British Columbia), Fair Trading Act (Alberta), or other applicable
                                consumer protection legislation. Where these Terms conflict with mandatory consumer protection laws,
                                the consumer protection laws shall prevail.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Severability</h2>
                            <p className="text-gray-700 mb-4">
                                If any provision of these Terms is found to be invalid or unenforceable by a court of competent jurisdiction,
                                the remaining provisions will remain in full force and effect, and the invalid provision shall be modified
                                to the minimum extent necessary to make it enforceable.
                            </p>
                        </section>

                        <section className="mb-8">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. Contact Information</h2>
                            <p className="text-gray-700 mb-4">
                                If you have any questions about these Terms, please contact us:
                            </p>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <ContactInfo
                                    variant="default"
                                    className="space-y-3"
                                    showPhone={false}
                                    showAddress={false}
                                    emailType='legal'
                                />
                            </div>
                        </section>
                    </div>
                </div>
            </div>
            </div>
        </>
    );
};